/**
 * React Hook for Thermal Printer
 * Easy-to-use hook for printing receipts
 */

import { useState, useCallback } from 'react'
import { useSelector } from 'react-redux'
import { filter, first, isEmpty } from 'lodash-es'
import { useIntl } from 'react-intl'
import {
  PRINT_METHODS,
  detectAvailablePrintMethods,
  getAvailablePrinters,
  setPreferredPrintMethod,
  clearPrinterPreference,
  printReceipt,
  mockReceiptData,
} from '../printer/thermalPrinterService'
import qzTrayPrinter from '../printer/qzTrayPrinter'
import {
  optionsSelector,
  printersSelector,
  printReceiptCountActiveSelector,
  selectedOutletSelector,
  selectedPosOutletOptionsSelector,
  unitsSelector,
} from '@/redux/selectors'

/**
 * Hook untuk thermal printer
 */
export const useThermalPrinter = (defaultOptions = {}) => {
  const { formatMessage } = useIntl()

  const outletOptions = useSelector(selectedPosOutletOptionsSelector)
  const companyOptions = useSelector(optionsSelector)
  const outlet = useSelector(selectedOutletSelector)

  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const [availableMethods, setAvailableMethods] = useState([])
  const [selectedMethod, setSelectedMethod] = useState(null)
  const [printers, setPrinters] = useState([])
  const [selectedPrinter, setSelectedPrinter] = useState(null)

  /**
   * Detect available print methods
   */
  const detectMethods = useCallback(async () => {
    try {
      const methods = await detectAvailablePrintMethods()

      setAvailableMethods(methods)

      // Set selected method to best available
      if (methods.length > 0 && !selectedMethod) {
        setSelectedMethod(methods[0].method)
      }

      return methods
    } catch (err) {
      console.error('Error detecting methods:', err)
      setError(err.message)
      return []
    }
  }, [selectedMethod])

  /**
   * Get available printers (QZ Tray only)
   */
  const getPrinters = useCallback(async () => {
    try {
      const printerList = await getAvailablePrinters()
      setPrinters(printerList)

      // Auto-select first printer if none selected
      if (printerList.length > 0 && !selectedPrinter) {
        setSelectedPrinter(printerList[0])
      }

      return printerList
    } catch (err) {
      console.error('Error getting printers:', err)
      return []
    }
  }, [selectedPrinter])

  /**
   * Print receipt
   */
  const print = useCallback(
    async (receiptData, options = {}) => {
      setIsLoading(true)
      setError(null)

      try {
        const printOptions = {
          ...defaultOptions,
          ...options,
          outletOptions,
          companyOptions,
          method: options.method || selectedMethod,
          printerName: options.printerName || selectedPrinter,
          outlet,
        }

        const result = await printReceipt(receiptData, printOptions, formatMessage)

        setIsLoading(false)
        return result
      } catch (err) {
        console.error('Print error:', err)
        setError(err.message)
        setIsLoading(false)
        throw err
      }
    },
    [
      defaultOptions,
      outletOptions,
      companyOptions,
      selectedMethod,
      selectedPrinter,
      outlet,
      formatMessage,
    ],
  )

  /**
   * Print with specific method
   */
  const printWithMethod = useCallback(
    async (method, receiptData, options = {}) => {
      return print(receiptData, { ...options, method, companyOptions, outletOptions })
    },
    [companyOptions, outletOptions, print],
  )

  /**
   * Test print
   */
  const testPrint = useCallback(
    async (options = {}) => {
      setIsLoading(true)
      setError(null)

      try {
        const result = await print(mockReceiptData, options)
        setIsLoading(false)
        return result
      } catch (err) {
        console.error('Test print error:', err)
        setError(err.message)
        setIsLoading(false)
        throw err
      }
    },
    [print],
  )

  /**
   * Save printer preference
   */
  const savePreference = useCallback((method, printerName = null) => {
    setPreferredPrintMethod(method, printerName)
    setSelectedMethod(method)
    setSelectedPrinter(printerName)
  }, [])

  /**
   * Clear printer preference
   */
  const clearPreference = useCallback(() => {
    clearPrinterPreference()
  }, [])

  /**
   * Initialize on mount
   */
  // useEffect(() => {
  //   detectMethods()
  // }, [detectMethods])

  return {
    // State
    isLoading,
    error,
    availableMethods,
    selectedMethod,
    printers,
    selectedPrinter,

    // Actions
    print,
    printWithMethod,
    testPrint,
    detectMethods,
    getPrinters,
    setSelectedMethod,
    setSelectedPrinter,
    savePreference,
    clearPreference,

    // Helpers
    isQZTrayAvailable: availableMethods.some((m) => m.method === PRINT_METHODS.QZ_TRAY),
    isBluetoothAvailable: availableMethods.some((m) => m.method === PRINT_METHODS.BLUETOOTH),
  }
}

/**
 * Simple hook - just print, no configuration
 */
export const useSimplePrint = () => {
  const { formatMessage } = useIntl()

  const outletOptions = useSelector(selectedPosOutletOptionsSelector)
  const companyOptions = useSelector(optionsSelector)
  const outlet = useSelector(selectedOutletSelector)
  const printers = useSelector(printersSelector)
  const units = useSelector(unitsSelector)
  const printReceiptCountActive = useSelector(printReceiptCountActiveSelector)

  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)

  const getFilteredPrinters = useCallback(() => {
    const outletPrinters = filter(printers || [], (row) => {
      if (!outlet?.id) {
        return true
      }
      return row?.outlet_id === outlet.id
    })
    return outletPrinters
  }, [printers, outlet])

  const print = useCallback(
    async (receiptData, options = {}) => {
      setIsLoading(true)
      setError(null)

      try {
        const result = await printReceipt(
          receiptData,
          {
            ...options,
            companyOptions,
            outletOptions,
            outlet,
            units: units || [],
          },
          formatMessage,
        )
        setIsLoading(false)
        return result
      } catch (err) {
        console.error('Print error:', err)
        setError(err.message)
        setIsLoading(false)
        throw err
      }
    },
    [companyOptions, outletOptions, outlet, units, formatMessage],
  )

  /**
   * Print QRIS payment receipt
   */
  const printQRIS = useCallback(
    async (qrisData, options = {}) => {
      setIsLoading(true)
      setError(null)

      try {
        // Filter printer sesuai outlet & printFunction yang aktif
        const outletPrinters = getFilteredPrinters()

        const filteredCurrentPrinters = filter(outletPrinters, (row) => {
          const functions = row?.print_functions || []
          return functions.some((fn) => fn.key === 'struk' && fn.active)
        })
        if (isEmpty(filteredCurrentPrinters)) {
          return {
            success: false,
            message: 'Tidak ada printer aktif',
            results: [],
          }
        }
        const firstPrinter = first(filteredCurrentPrinters)
        await printReceipt(
          qrisData,
          {
            ...options,
            companyOptions,
            outletOptions,
            outlet,
            qris: true,
            printerName: firstPrinter.printer,
            print_connection: firstPrinter.print_connection,
            print_ip: firstPrinter.print_ip,
            print_port: firstPrinter.print_port,
          },
          formatMessage,
        )

        setIsLoading(false)
        return {
          success: true,
          message: 'QRIS berhasil dicetak',
        }
      } catch (err) {
        console.error('Print QRIS error:', err)
        setError(err.message)
        setIsLoading(false)
        throw err
      }
    },
    [getFilteredPrinters, companyOptions, outletOptions, outlet, formatMessage],
  )

  /**
   * Print Web Order QR code receipt
   */
  const printWebOrder = useCallback(
    async (webOrderData, options = {}) => {
      setIsLoading(true)
      setError(null)

      try {
        // Filter printer sesuai outlet & printFunction yang aktif
        const outletPrinters = getFilteredPrinters()

        const filteredCurrentPrinters = filter(outletPrinters, (row) => {
          const functions = row?.print_functions || []
          return functions.some((fn) => fn.key === 'struk' && fn.active)
        })
        if (isEmpty(filteredCurrentPrinters)) {
          return {
            success: false,
            message: 'Tidak ada printer aktif',
            results: [],
          }
        }
        const firstPrinter = first(filteredCurrentPrinters)
        await printReceipt(
          webOrderData,
          {
            ...options,
            companyOptions,
            outletOptions,
            outlet,
            webOrder: true,
            printerName: firstPrinter.printer,
            print_connection: firstPrinter.print_connection,
            print_ip: firstPrinter.print_ip,
            print_port: firstPrinter.print_port,
          },
          formatMessage,
        )

        setIsLoading(false)
        return {
          success: true,
          message: 'Web Order QR Code berhasil dicetak',
        }
      } catch (err) {
        console.error('Print Web Order error:', err)
        setError(err.message)
        setIsLoading(false)
        throw err
      }
    },
    [getFilteredPrinters, companyOptions, outletOptions, outlet, formatMessage],
  )

  /**
   * Print Shift Detail receipt
   */
  const printShiftDetail = useCallback(
    async (shiftData, options = {}) => {
      setIsLoading(true)
      setError(null)

      try {
        // Filter printer sesuai outlet & printFunction yang aktif
        const outletPrinters = getFilteredPrinters()

        const filteredCurrentPrinters = filter(outletPrinters, (row) => {
          const functions = row?.print_functions || []
          return functions.some((fn) => fn.key === 'struk' && fn.active)
        })
        if (isEmpty(filteredCurrentPrinters)) {
          return {
            success: false,
            message: 'Tidak ada printer aktif',
            results: [],
          }
        }
        const firstPrinter = first(filteredCurrentPrinters)
        await printReceipt(
          shiftData,
          {
            ...options,
            companyOptions,
            outletOptions,
            outlet,
            shiftDetail: true,
            paperWidth: firstPrinter.paper_size,
            printerName: firstPrinter.printer,
            print_connection: firstPrinter.print_connection,
            print_ip: firstPrinter.print_ip,
            print_port: firstPrinter.print_port,
          },
          formatMessage,
        )

        setIsLoading(false)
        return {
          success: true,
          message: 'Laporan Shift berhasil dicetak',
        }
      } catch (err) {
        console.error('Print Shift Detail error:', err)
        setError(err.message)
        setIsLoading(false)
        throw err
      }
    },
    [getFilteredPrinters, companyOptions, outletOptions, outlet, formatMessage],
  )

  const checkAutoPrint = useCallback(
    (printFunction) => {
      const outletPrinters = getFilteredPrinters()
      if (isEmpty(outletPrinters)) {
        return false
      }
      let autoPrintKey = ''
      if (printFunction === 'struk') {
        autoPrintKey = 'auto_print_invoice'
      } else if (printFunction === 'bill') {
        autoPrintKey = 'auto_print_order'
      }
      const filteredCurrentPrinters = filter(outletPrinters, (row) => {
        const functions = row?.print_functions || []
        return functions.some((fn) => fn.active && fn[autoPrintKey])
      })
      return !isEmpty(filteredCurrentPrinters)
    },
    [getFilteredPrinters],
  )

  const handleJobPrint = useCallback(
    async ({ data, printFunction, isAutoPrint = false }) => {
      const outletPrinters = getFilteredPrinters()

      let autoPrintKey = ''
      if (isAutoPrint) {
        if (printFunction === 'struk') {
          autoPrintKey = 'auto_print_invoice'
        } else if (printFunction === 'bill') {
          autoPrintKey = 'auto_print_order'
        }
      }

      // Filter printer berdasarkan trigger: auto_print_order (bill) atau auto_print_invoice (struk)
      const filteredCurrentPrinters = filter(outletPrinters, (row) => {
        const functions = row?.print_functions || []
        if (isAutoPrint) {
          return functions.some((fn) => fn.active && fn[autoPrintKey])
        }
        return functions.some((fn) => fn.key === printFunction && fn.active)
      })

      if (isEmpty(filteredCurrentPrinters)) {
        return {
          success: false,
          message: `Tidak ada printer aktif untuk fungsi "${printFunction}"`,
          results: [],
        }
      }

      // Build jobs: untuk autoPrint, satu job per (printer, print_function aktif yg match trigger)
      // Untuk manual print, satu job per printer untuk printFunction tersebut
      const jobs = []
      filteredCurrentPrinters.forEach((row) => {
        const functions = row?.print_functions || []

        let functionsToPrint = []
        if (isAutoPrint) {
          // Ambil semua print_function yang active DAN match trigger (auto_print_order/auto_print_invoice)
          functionsToPrint = functions.filter((fn) => fn.active && fn[autoPrintKey])
        } else {
          // Manual: hanya fungsi yang dipilih
          const fn = functions.find((f) => f.key === printFunction && f.active)
          if (fn) {
            functionsToPrint = [fn]
          }
        }

        functionsToPrint.forEach((fn) => {
          const enableCut = Boolean(fn?.auto_cut)
          const enableOpenCashDrawer = Boolean(fn?.auto_open_cash_drawer)
          const copies = Math.max(1, Number(fn?.number_of_copies) || 1)

          jobs.push({
            printerName: row?.printer,
            receiptData: data,
            printOptions: {
              printerFunction: fn.key, // struk, bill, checker, kitchen, atau labeling
              outlet,
              companyOptions,
              outletOptions,
              printReceiptCountActive,
              paperWidth: row?.paper_size,
              enableCut,
              enableOpenCashDrawer,
              units: units || [],
              print_connection: row?.print_connection,
              print_ip: row?.print_ip,
              print_port: row?.print_port,
              print_command: row?.print_command?.name || row?.print_command,
              print_variant: row?.print_variant?.name || row?.print_variant,
            },
            config: {
              copies,
              jobName: `${fn.key}${data?.ref_number ? ` #${data.ref_number}` : ''}`,
            },
          })
        })
      })

      if (isEmpty(jobs)) {
        return {
          success: false,
          message: `Tidak ada printer aktif untuk fungsi "${printFunction}"`,
          results: [],
        }
      }

      return qzTrayPrinter.printChained(jobs, { continueOnError: true }, formatMessage)
    },
    [
      getFilteredPrinters,
      formatMessage,
      outlet,
      companyOptions,
      outletOptions,
      units,
      printReceiptCountActive,
    ],
  )

  return {
    print,
    isLoading,
    error,
    onJobPrint: handleJobPrint,
    printQRIS,
    printWebOrder,
    printShiftDetail,
    checkAutoPrint,
  }
}

export default useThermalPrinter
